import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {
  fetchStationGeneralSettings,
  getListenerCount,
  updateStationGeneralSettings,
} from '../../api/station'
import { useSettingsIntegrations } from '../Settings'
import useListenerCountStore from '../../store/listenerCount'
import useStationListenerCountSettingsStore from '../../store/stationListenerCounts'
import useStationGeneralSettingsStore from '../../store/stationGeneralSettings'
import { usePageTitle } from '../../components/common/PageTitleContext'
import { parseListenerPlainText } from '../../utils/listenerCount'
import { withListenerDebug } from '../../utils/listenerDebug'

type TestFetchResult = {
  fetchedAt: string
  httpStatus: number | null
  cache: string | null
  rawText: string | null
  serverCount: number | null
  clientCount: number | null
  lastUpdated: string | null
  stale: boolean | null
}

const StationGeneralSettingsPage: React.FC = () => {
  usePageTitle('General settings')
  const { stationId, notify } = useSettingsIntegrations()
  const setListenerCountState = useListenerCountStore((state) => state.setState)
  const bumpListenerCountVersion = useListenerCountStore((state) => state.bumpVersion)
  const setStationListenerCountSettings = useStationListenerCountSettingsStore(
    (state) => state.setListenerCountSettings,
  )
  const setStationGeneralSettings = useStationGeneralSettingsStore((state) => state.setGeneralSettings)

  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [showListenerCounts, setShowListenerCounts] = React.useState(false)
  const [endpoint, setEndpoint] = React.useState('')
  const [autoSendQuickReplies, setAutoSendQuickReplies] = React.useState(false)
  const [enterToSend, setEnterToSend] = React.useState(false)
  const [validationError, setValidationError] = React.useState<string | null>(null)
  const [testLoading, setTestLoading] = React.useState(false)
  const [testError, setTestError] = React.useState<string | null>(null)
  const [testResult, setTestResult] = React.useState<TestFetchResult | null>(null)

  const resetForm = React.useCallback(() => {
    setShowListenerCounts(false)
    setEndpoint('')
    setAutoSendQuickReplies(false)
    setEnterToSend(false)
    setValidationError(null)
    setTestError(null)
    setTestResult(null)
    setTestLoading(false)
  }, [])

  React.useEffect(() => {
    setLoadError(null)
    setValidationError(null)

    if (!stationId) {
      resetForm()
      setListenerCountState({
        enabled: false,
        count: null,
        lastUpdated: null,
        stale: null,
        loading: false,
        hasFetched: false,
        error: null,
        nextRefreshAt: null,
      })
      return
    }

    let cancelled = false
    setLoading(true)

    fetchStationGeneralSettings(stationId)
      .then((response) => {
        if (cancelled) {
          return
        }

        const enabled = Boolean(response.show_listener_counts && response.listener_count_endpoint)
        setShowListenerCounts(enabled)
        setEndpoint(response.listener_count_endpoint)
        const autoSend = Boolean(response.auto_send_quick_replies)
        setAutoSendQuickReplies(autoSend)
        const enter = Boolean(response.enter_to_send)
        setEnterToSend(enter)
        setListenerCountState({ enabled })
        setStationListenerCountSettings(stationId, {
          enabled,
          endpoint: enabled ? response.listener_count_endpoint || null : null,
        })
        setStationGeneralSettings(stationId, {
          autoSendQuickReplies: autoSend,
          enterToSend: enter,
        })
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }
        const message = error instanceof Error ? error.message : 'Unable to load station settings.'
        setLoadError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    resetForm,
    setListenerCountState,
    setStationListenerCountSettings,
    setStationGeneralSettings,
    stationId,
  ])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!stationId) {
      return
    }

    setValidationError(null)

    const trimmedEndpoint = endpoint.trim()

    if (showListenerCounts) {
      if (!trimmedEndpoint) {
        setValidationError('Enter the listener count endpoint URL.')
        return
      }

      if (!isValidUrl(trimmedEndpoint)) {
        setValidationError('Enter a valid http(s) URL.')
        return
      }
    }

    setSaving(true)

    try {
      const response = await updateStationGeneralSettings(stationId, {
        show_listener_counts: showListenerCounts,
        listener_count_endpoint: showListenerCounts ? trimmedEndpoint : '',
        auto_send_quick_replies: autoSendQuickReplies,
        enter_to_send: enterToSend,
      })

      const enabled = Boolean(response.show_listener_counts && response.listener_count_endpoint)
      setShowListenerCounts(enabled)
      setEndpoint(response.listener_count_endpoint)
      const autoSend = Boolean(response.auto_send_quick_replies)
      setAutoSendQuickReplies(autoSend)
      const enter = Boolean(response.enter_to_send)
      setEnterToSend(enter)
      setListenerCountState({
        enabled,
        count: null,
        lastUpdated: null,
        stale: null,
        loading: false,
        hasFetched: false,
        error: null,
        nextRefreshAt: null,
      })

      setStationListenerCountSettings(stationId, {
        enabled,
        endpoint: enabled ? response.listener_count_endpoint || null : null,
      })
      setStationGeneralSettings(stationId, {
        autoSendQuickReplies: autoSend,
        enterToSend: enter,
      })

      bumpListenerCountVersion()

      notify('success', 'General settings saved.')
    } catch (error) {
      if (isRestError(error) && error.status === 422) {
        const message = error.message || 'Listener Count Endpoint URL must be http(s).'
        setValidationError(message)
        return
      }

      const message = error instanceof Error ? error.message : 'Unable to save settings.'
      notify('error', message)
    } finally {
      setSaving(false)
    }
  }

  const handleTestFetch = React.useCallback(async () => {
    if (!stationId) {
      setTestError('Select a station before testing the listener count endpoint.')
      return
    }

    setTestError(null)
    setTestResult(null)
    setTestLoading(true)

    try {
      const response = await getListenerCount(stationId, { force: true, debug: true })
      const rawText = typeof response.raw_text === 'string' ? response.raw_text : null
      const clientParsed = rawText ? parseListenerPlainText(rawText) : null
      const result: TestFetchResult = {
        fetchedAt: new Date().toISOString(),
        httpStatus: response.http_status ?? null,
        cache: response.cache ?? null,
        rawText,
        serverCount: response.count ?? null,
        clientCount: clientParsed,
        lastUpdated: response.last_updated,
        stale: typeof response.stale === 'boolean' ? response.stale : null,
      }

      setTestResult(result)
      withListenerDebug('settings:test-fetch', (log) => {
        log.info('Listener count test fetch result', result)
      })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to test listener count endpoint.'
      setTestError(message)
      withListenerDebug('settings:test-fetch', (log) => {
        log.error('Listener count test fetch failed', error)
      })
    } finally {
      setTestLoading(false)
    }
  }, [stationId])

  React.useEffect(() => {
    if (!showListenerCounts) {
      setTestError(null)
      setTestResult(null)
      setTestLoading(false)
    }
  }, [showListenerCounts])

  React.useEffect(() => {
    setTestError(null)
    setTestResult(null)
  }, [endpoint])

  if (!stationId) {
    return <Alert severity="info">Select a station to manage general settings.</Alert>
  }

  return (
    <Stack spacing={3} component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={0.5}>
        <Typography variant="h5" component="h2">
          General
        </Typography>
        <Typography color="text.secondary">
          Configure station-wide options for visibility and real-time status indicators.
        </Typography>
      </Stack>

      {loadError ? <Alert severity="error">{loadError}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <FormControl component="fieldset" variant="standard">
            <FormControlLabel
              control={
                <Switch
                  checked={autoSendQuickReplies}
                  onChange={(event) => setAutoSendQuickReplies(event.target.checked)}
                  disabled={loading || saving}
                />
              }
              label="Auto Send Quick Replies"
            />
            <FormHelperText>
              Send the selected quick reply immediately without pressing Send.
            </FormHelperText>
          </FormControl>

          <FormControl component="fieldset" variant="standard">
            <FormControlLabel
              control={
                <Switch
                  checked={enterToSend}
                  onChange={(event) => setEnterToSend(event.target.checked)}
                  disabled={loading || saving}
                />
              }
              label="Enter to Send"
            />
            <FormHelperText>
              Press Enter to send the message. Press Shift+Enter for a new line.
            </FormHelperText>
          </FormControl>

          <FormControl component="fieldset" variant="standard">
            <FormControlLabel
              control={
                <Switch
                  checked={showListenerCounts}
                  onChange={(event) => {
                    setShowListenerCounts(event.target.checked)
                    if (!event.target.checked) {
                      setValidationError(null)
                    }
                  }}
                  disabled={loading || saving}
                />
              }
              label="Show listener counts"
            />
            <FormHelperText>
              Surface the current listener count in the app header for presenters.
            </FormHelperText>
          </FormControl>

          {showListenerCounts ? (
            <Stack spacing={2}>
              <TextField
                label="Listener count endpoint URL"
                type="url"
                value={endpoint}
                onChange={(event) => setEndpoint(event.target.value)}
                fullWidth
                disabled={loading || saving}
                error={Boolean(validationError)}
                helperText={
                  validationError ?? 'Provide an http(s) endpoint returning the listener count.'
                }
                required
              />

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={() => {
                    void handleTestFetch()
                  }}
                  disabled={
                    testLoading ||
                    loading ||
                    saving ||
                    !stationId
                  }
                >
                  Test fetch
                </Button>
                {testLoading ? <CircularProgress size={20} /> : null}
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Test fetch uses the saved listener count endpoint. Save changes before testing new
                URLs.
              </Typography>

              {testError ? <Alert severity="error">{testError}</Alert> : null}

              {testResult ? (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2">Last test result</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fetched at {new Date(testResult.fetchedAt).toLocaleString()}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                        gap: 1.5,
                      }}
                    >
                      {[
                        { label: 'HTTP status', value: testResult.httpStatus ?? '—' },
                        { label: 'Cache', value: testResult.cache ?? '—' },
                        { label: 'Server parsed', value: testResult.serverCount ?? '—' },
                        { label: 'Client parsed', value: testResult.clientCount ?? '—' },
                        { label: 'Last updated', value: testResult.lastUpdated ?? '—' },
                        {
                          label: 'Stale',
                          value:
                            testResult.stale === null
                              ? '—'
                              : testResult.stale
                                ? 'Yes'
                                : 'No',
                        },
                      ].map((entry) => (
                        <Box key={entry.label}>
                          <Typography variant="body2" color="text.secondary">
                            {entry.label}
                          </Typography>
                          <Typography variant="body2">{entry.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Raw response
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        p: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {testResult.rawText ?? '—'}
                    </Box>
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
        <Button type="submit" variant="contained" disabled={saving || loading}>
          Save changes
        </Button>
        {saving ? <CircularProgress size={20} /> : null}
      </Stack>
    </Stack>
  )
}

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (error) {
    return false
  }
}

function isRestError(error: unknown): error is Error & { status?: number; data?: unknown } {
  return typeof error === 'object' && error !== null && 'status' in error
}

export default StationGeneralSettingsPage
